import { useState, useRef, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import ContactFormModal from './admin/ContactFormModal';
import { cn } from '@/lib/utils';

export default function InlineContactSearch({
  value,
  onChange,
  contactType,
  onSelect,
  placeholder = 'Search or type...',
  disabled = false,
  onShowAddForm,
}) {
  const [search, setSearch] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!search || search.length < 1) {
      setResults([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        // Shipper/Consignee are the same kind of contact (a physical
        // location) -- the same company is routinely a shipper on one
        // load and a consignee on another, so searching one field
        // should still surface a contact saved under the other type.
        // Same idea as Customer/Broker below.
        const TYPE_GROUPS = {
          Customer: ['Customer', 'Broker'],
          Shipper: ['Shipper', 'Consignee'],
          Consignee: ['Consignee', 'Shipper'],
        };
        const contactTypeFilter = TYPE_GROUPS[contactType] || [contactType];
        const allContacts = await client.entities.Contact.list('-created_date', 100);
        if (cancelled) return; // a newer keystroke already superseded this search
        const filtered = allContacts.filter(
          c => contactTypeFilter.includes(c.type) && c.name.toLowerCase().includes(search.toLowerCase())
        );
        setResults(filtered);
        setOpen(true);
      } catch (err) {
        if (cancelled) return;
        console.error('Contact search error:', err);
        setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; };
  }, [search, contactType]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (contact) => {
    if (contact.is_blacklisted) {
      toast.warning(`${contact.name} is blacklisted — double-check before booking this load.`, { duration: 6000 });
    }
    setSearch(contact.name);
    onChange(contact.name);
    onSelect({
      id: contact.id,
      name: contact.name,
      contact: contact.contact || '',
      phone: contact.primary_phone || '',
      email: contact.email || '',
      street: contact.street || '',
      city: contact.city || '',
      state: contact.state || '',
      zip: contact.zip_code || '',
      zip_code: contact.zip_code || '',
      address: [contact.street, contact.city, contact.state, contact.zip_code].filter(Boolean).join(', '),
    });
    setOpen(false);
  };

  const handleAddNew = () => {
    if (onShowAddForm) {
      onShowAddForm(contactType, search);
    } else {
      setShowForm(true);
    }
  };

  const handleFormSaved = async (newContact) => {
    if (newContact && newContact.name) {
      handleSelect(newContact);
    }
    setShowForm(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Input
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            onChange(e.target.value);
          }}
          onFocus={() => search && setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="text-foreground pr-9"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {open && (search || results.length > 0) && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-56 overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
          {results.length > 0 ? (
            <>
              {results.map(contact => (
                <button
                  key={contact.id}
                  onClick={() => handleSelect(contact)}
                  className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-white/5 border-b border-border last:border-b-0 transition-colors"
                >
                  <p className="font-medium flex items-center gap-1.5">
                    {contact.name}
                    {contact.is_blacklisted && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-400">
                        BLACKLISTED
                      </span>
                    )}
                  </p>
                  {contact.primary_phone && <p className="text-xs text-muted-foreground">{contact.primary_phone}</p>}
                </button>
              ))}
              {/* An exact (case-insensitive) name match already exists in
                  the results above -- hide "Add to Address Book" so this
                  can't create a duplicate contact for the same company. A
                  near-match (e.g. "TQL Logistics" vs "TQL") still shows it,
                  since that may genuinely be a different company. */}
              {!results.some(c => c.name.trim().toLowerCase() === search.trim().toLowerCase()) && (
                <button
                  onClick={handleAddNew}
                  className="w-full px-3 py-2 text-left text-xs font-medium text-primary hover:bg-primary/10 border-t border-border flex items-center gap-2 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Add "{search}" to Address Book
                </button>
              )}
            </>
          ) : (
            <div className="px-3 py-2">
              {loading ? (
                <p className="text-xs text-muted-foreground">Searching...</p>
              ) : (
                <button
                  onClick={handleAddNew}
                  className="w-full text-left text-xs font-medium text-primary hover:text-primary/80 flex items-center gap-2 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Add "{search}" to Address Book
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {!onShowAddForm && showForm && (
        <ContactFormModal
          contact={{
            name: search,
            type: contactType === 'Customer' ? 'Customer' : contactType,
          }}
          onClose={() => setShowForm(false)}
          onSaved={handleFormSaved}
        />
      )}
    </div>
  );
}